import React, { useRef, useState, useMemo } from "react";
import { useQuery } from "react-query"; // or "@tanstack/react-query" if using v4+
import "../../Styles/overview2.css";
import { ChevronLeft, ChevronRight } from "lucide-react";

import {
  ExternalLink
} from "lucide-react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import useAxiosInstance from "../../lib/useAxiosInstance";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

const OverviewRight = () => {
  const { t } = useTranslation();
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const initialView = calendarView;
  const calendarRef = useRef(null);
  const [currentView, setCurrentView] = useState(initialView);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [headerDate, setHeaderDate] = useState(new Date());
  const axiosInstance = useAxiosInstance();

  const fetchEvents = async () => {
    const response = await axiosInstance.get("api/eventScheduler/events");
    return response.data;
  };

  const { data, isLoading, error, isSuccess, refetch } = useQuery(
    ["Event-List"],
    fetchEvents,
    {
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );
  const events = useMemo(() => {
    if (!data?.status || !data?.data?.events) return [];
    return data.data.events;
  }, [data]);
  // console.log("events",events)
  const { data: dndEntries = [], refetch: refetchDND } = useQuery(
    "DND-List",
    async () => {
      const res = await axiosInstance.get("api/eventScheduler/getDND");
      return res?.data?.data || [];
    }
  );

  const mappedEvents = events
    .filter((e) => e.status === "pending" || e.status === "approved")
    .map((e) => {
      // Status color mapping
      const statusColors = {
        pending: {
          backgroundColor: "#FDE68A",
          borderColor: "#FBBF24",
          textColor: "#92400E",
        },
        approved: {
          backgroundColor: "#15803D",
          borderColor: "#166534",
          textColor: "#D1FAE5",
        },
      };
      // Dot color for typeOfEvent (used in Day view dots)
      const dotColors = {
        visit: "#3B82F6", // blue
        invitation: "#EC4899", // pink
        appointment: "#10B981", // green
      };

      const statusColor = statusColors[e.status] || {
        backgroundColor: "#D1D5DB",
        borderColor: "#9CA3AF",
      };

      return {
        id: e._id,
        title: `${e.eventTitle} (${e.typeOfEvent})`,
        start: e.start,
        end: e.end,
        textColor: "#ffffff",
        extendedProps: {
          typeOfEvent: e.typeOfEvent,
          dotColor: dotColors[e.typeOfEvent],
          status: e.status,
        },
        ...statusColor,
      };
    });

  // Add DND entries as events
  const dndEvents = dndEntries.map((dnd) => ({
    id: `dnd-${dnd._id}`,
    title: "Blocked",
    start: dnd.start,
    end: dnd.end,
    backgroundColor: "#9CA3AF",
    borderColor: "#6B7280",
    textColor: "#374151",
    display: "background",
    classNames: ["dnd-event"],
    extendedProps: {
      isDND: true,
    },
  }));
  const allEvents = [...mappedEvents, ...dndEvents];

  const today = new Date();
const todayStart = new Date(today.setHours(0, 0, 0, 0));
const todayEnd = new Date(today.setHours(23, 59, 59, 999));

const todayTasks = events
  .filter((e) => {
    const startDate = new Date(e.start);
    return startDate >= todayStart && startDate <= todayEnd;
  })
  .map((e) => {
    const now = new Date();
    const isCompleted = new Date(e.end) < now;

    return {
      id: e._id,
      title: e.eventTitle,
      time: new Date(e.start).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: isCompleted ? "completed" : "upcoming",
      type: e.typeOfEvent || "event",
    };
  });
// You can toggle this to show/hide tasks
const hasTasks = todayTasks.length > 0;


  const handlePrevClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.prev();
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

  const handleNextClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.next();
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

  const handleTodayClick = () => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.today();
    const today = calendarApi?.getDate() || new Date();
    setCurrentDate(today);
    setHeaderDate(today);
  };
  const getMonthYear = () =>
    headerDate.toLocaleDateString("default", {
      month: "long",
      year: "numeric",
    });

  const getFullDateWithDay = () =>
    headerDate.toLocaleDateString("default", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const handleViewChange = (view) => {
    const calendarApi = calendarRef.current?.getApi();
    calendarApi?.changeView(view);
    const newDate = calendarApi?.getDate() || new Date();
    setCurrentView(view);
    setCurrentDate(newDate);
    setHeaderDate(newDate);
  };

  const handleDateClick = (info) => {
    // When a date is clicked in month view, switch to day view for that date
    if (currentView === "dayGridMonth") {
      const calendarApi = calendarRef.current?.getApi();
      calendarApi?.changeView("timeGridDay", info.date);
      setCurrentView("timeGridDay");
      setCurrentDate(info.date);
      setHeaderDate(info.date);
    }
  };

  return (
    <>
      <div className="space-y-4 bg-transparent rounded-xl">
        {/* Today's Schedule */}
        <div className="grid grid-cols-1 flex-1 gap-6 rounded-2xl h-[37%] z-[-1] relative">
          <div className="flex flex-col transform hover:shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] relative card-wrapper">
            <div className="flex-between">
              <h3 className="card-heading">
                {t("Dashboard.todaysSchedule")}
              </h3>
              <div className="flex space-x-2">
               
                <NavLink to="/eventScheduler" className="hover:text-blue-600">
                    <button className="p-1 hover:bg-white/20 rounded text-gray-600 transition-all duration-200">
                      <ExternalLink size={16} />
                    </button>
                </NavLink>
              </div>
            </div>
            <div className="flex-1 justify-center relative bg-white/30 rounded-lg p-0">
              {hasTasks ? (
                <div className="space-y-3 max-h-[12rem] overflow-y-auto overflow-x-hidden">
                  {todayTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center justify-between p-2 bg-white/50 rounded-lg hover:bg-white/70 transition-colors min-w-0"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div
                          className={`w-3 h-3 rounded-full flex-shrink-0 ${
                            task.status === "completed"
                              ? "bg-green-500"
                              : task.status === "upcoming"
                              ? "bg-blue-500"
                              : "bg-gray-400"
                          }`}
                        ></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {task.title}
                          </p>
                          <p className="text-xs text-gray-600 capitalize truncate">
                            {task.type}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="text-xs font-semibold text-gray-700">
                          {task.time}
                        </p>
                        <span
                          className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                            task.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : task.status === "upcoming"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-gray-800 font-medium">
                    {t("Dashboard.noTasksToday")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar Body */}
        <div className="transform hover:shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] relative card-wrapper">
          <div className="calendar-container backdrop-blur-xl lg:col-span-2 transition-all duration-300 transform hover:shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] cursor-pointer text-black p-2 relative overflow-hidden bg-gray-300/20 border border-gray-200 rounded-2xl">
            <div className="flex-between">
              <div className="flex items-center space-x-4">
                  {/* <div className="p-2 bg-blue-500/10 rounded-xl backdrop-blur-sm">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div> */}
                <div>
                  <p className="text-md subheading font-medium">
                    {getMonthYear()}
                  </p>
                  <p className="text-sm subheading">{getFullDateWithDay()}</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={handleTodayClick}
                  className="px-1 py-1 bg-white/20 hover:bg-white/30 rounded-lg subheading transition-all duration-200 backdrop-blur-sm border border-white/30"
                >
                  Today
                </button>
                <button
                  onClick={handlePrevClick}
                  className="p-1 bg-white/20 hover:bg-white/30 rounded-lg subheading transition-all duration-200 backdrop-blur-sm border border-white/30"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNextClick}
                  className="p-1 bg-white/20 hover:bg-white/30 rounded-lg subheading transition-all duration-200 backdrop-blur-sm border border-white/30"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex flex-col items-center">
              {/* <p className="text-sm pl-1 pb-1 subheading">
                {getFullDateWithDay()}
              </p> */}

              {/* View Controls */}
              <div className="flex items-center space-x-2 mb-1">
                {["dayGridMonth", "timeGridWeek", "timeGridDay"].map((view) => (
                  <button
                    key={view}
                    onClick={() => handleViewChange(view)}
                    className={`px-2 rounded-lg transition-all duration-200 ${
                      currentView === view
                        ? "buttonHeading"
                        : "bg-white/20 hover:bg-white/30 subheading border border-white/30 transition-all duration-300 transform hover:scale-[1.01] hover:shadow-xl hover:border-blue-300/40"
                    }`}
                  >
                    {view === "dayGridMonth"
                      ? "Month"
                      : view === "timeGridWeek"
                      ? "Week"
                      : view === "timeGridDay"
                      ? "Day"
                      : "List"}
                  </button>
                ))}
              </div>
            </div>

            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                interactionPlugin,
                listPlugin,
              ]}
              initialView={currentView}
              events={allEvents}
              selectable={true}
              selectMirror={true}
              headerToolbar={false}
              height={220}
              dayMaxEvents={true}
              weekends={true}
              dayHeaderClassNames="font-semibold"
              eventClassNames="cursor-pointer hover:opacity-80 transition-opacity"
              slotMinTime="06:00:00"
              slotMaxTime="22:00:00"
              allDaySlot={false}
              dateClick={handleDateClick}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OverviewRight;
